import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { deleteUserByEmail } from "@/utils/adminUserDeletion";

const emailSchema = z.string().trim().email({ message: "Enter a valid email" }).max(255);

export default function AdminDeleteUser() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onDelete = async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast({ title: "Invalid email", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }

    const confirmed = confirm(`Delete user ${parsed.data}?\n\nThis cannot be undone.`);
    if (!confirmed) return;

    setLoading(true);
    try {
      const res = await deleteUserByEmail(parsed.data);
      toast({ title: "User deleted", description: res?.message || `Deleted ${parsed.data}` });
      setEmail("");
    } catch (err: any) {
      console.error("Delete user error:", err);
      toast({ title: "Error", description: err?.message || "Failed to delete user", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Delete User | Admin</title>
        <meta name="description" content="Admin delete user by email" />
        <link rel="canonical" href={`${window.location.origin}/admin-delete-user`} />
      </Helmet>

      <div className="max-w-xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Delete User (Admin)</h1>

        <Card>
          <CardHeader>
            <CardTitle>Delete a user account</CardTitle>
            <CardDescription>Enter the user's email to permanently remove their account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                aria-label="User email"
              />
              <Button onClick={onDelete} disabled={loading || !email.trim()} variant="destructive">
                {loading ? "Deleting..." : "Delete User"}
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">Tip: Use glennmctag1@pbsbusiness.com.au for the reset you requested.</div>
            <div>
              <Button asChild variant="secondary">
                <Link to="/admin-setup">Back to Admin Setup</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
